/**
 * Ops ownership endpoints (phase 1 contract §3). Base: /api/v1/admin.
 * Endpoints marked (M) require ROLE_OPS_MANAGER — the UI hides them for
 * executives, the API enforces with 403.
 */
import { api } from '../client';
import type {
  AppId,
  AppPlatform,
  AppVersionPolicyEntry,
  AppVersionPolicyUpdate,
  CountResponse,
  DefaultMaxOpenOrdersResponse,
  OpsUser,
  OpsUserStatus,
  OwnershipEvent,
  PagedResponse,
  ShipmentResponse,
  ShipperAccountManagerResponse,
  SupportPhoneResponse,
  ScorecardTargets,
  OpsScorecardMeResponse,
  OpsTeamScorecardResponse,
  TodayBoardResponse,
  TodayScope,
} from '../types';

// ─── Ops users ────────────────────────────────────────────────────────────────

/** Current caller (upserted from token claims on every call). */
export async function getMyOpsUser(): Promise<OpsUser> {
  const { data } = await api.get<OpsUser>('/admin/ops-users/me');
  return data;
}

/** All non-INACTIVE ops users, sorted by first name. */
export async function getOpsUsers(): Promise<OpsUser[]> {
  const { data } = await api.get<OpsUser[]>('/admin/ops-users');
  return data;
}

/** (M) */
export async function updateOpsUserStatus(
  id: number,
  status: Exclude<OpsUserStatus, 'INACTIVE'>,
): Promise<OpsUser> {
  const { data } = await api.patch<OpsUser>(`/admin/ops-users/${id}/status`, { status });
  return data;
}

/** (M) 1..500, or null to fall back to the platform default. */
export async function updateOpsUserMaxOpenOrders(
  id: number,
  maxOpenOrders: number | null,
): Promise<OpsUser> {
  const { data } = await api.patch<OpsUser>(`/admin/ops-users/${id}/max-open-orders`, {
    maxOpenOrders,
  });
  return data;
}

// ─── Shipment ownership ──────────────────────────────────────────────────────

export async function claimShipment(shipmentId: number): Promise<ShipmentResponse> {
  const { data } = await api.post<ShipmentResponse>(`/admin/shipments/${shipmentId}/claim`);
  return data;
}

/** Owner or manager only. Reason: 3..500 chars. */
export async function releaseShipment(
  shipmentId: number,
  reason: string,
): Promise<ShipmentResponse> {
  const { data } = await api.post<ShipmentResponse>(`/admin/shipments/${shipmentId}/release`, {
    reason,
  });
  return data;
}

/** (M) ASSIGN when unowned, REASSIGN when owned. Target must be ACTIVE. */
export async function assignShipment(
  shipmentId: number,
  opsUserId: number,
  reason: string,
): Promise<ShipmentResponse> {
  const { data } = await api.post<ShipmentResponse>(`/admin/shipments/${shipmentId}/assign-owner`, {
    opsUserId,
    reason,
  });
  return data;
}

/** Newest first. */
export async function getOwnershipEvents(shipmentId: number): Promise<OwnershipEvent[]> {
  const { data } = await api.get<OwnershipEvent[]>(
    `/admin/shipments/${shipmentId}/ownership-events`,
  );
  return data;
}

// ─── Work queues ─────────────────────────────────────────────────────────────

export interface WorkQueueParams {
  page?: number;
  size?: number;
}

/** Caller's open orders — open stop first (dnm-work#165 C5), then oldest ownedSince. */
export async function getMyWork(
  params: WorkQueueParams = {},
): Promise<PagedResponse<ShipmentResponse>> {
  const { data } = await api.get<PagedResponse<ShipmentResponse>>('/admin/work/mine', { params });
  return data;
}

/** Open & unowned — oldest createdAt first. */
export async function getUnassignedWork(
  params: WorkQueueParams = {},
): Promise<PagedResponse<ShipmentResponse>> {
  const { data } = await api.get<PagedResponse<ShipmentResponse>>('/admin/work/unassigned', {
    params,
  });
  return data;
}

export async function getUnassignedCount(): Promise<number> {
  const { data } = await api.get<CountResponse>('/admin/work/unassigned/count');
  return data.count;
}

/** The ops "Today" board (#299, Contract dnm-work#151): what is waiting, oldest first. Demo world only for now. */
export async function getTodayBoard(scope: TodayScope = 'mine'): Promise<TodayBoardResponse> {
  const { data } = await api.get<TodayBoardResponse>('/admin/work/today', { params: { scope } });
  return data;
}

// ─── Shipper account manager ─────────────────────────────────────────────────

/** shipperProfileId == the shipper's user id (shipper_profiles uses @MapsId). */
export async function getShipperAccountManager(
  shipperProfileId: number,
): Promise<ShipperAccountManagerResponse> {
  const { data } = await api.get<ShipperAccountManagerResponse>(
    `/admin/shippers/${shipperProfileId}/account-manager`,
  );
  return data;
}

/** (M) null clears the account manager. */
export async function setShipperAccountManager(
  shipperProfileId: number,
  opsUserId: number | null,
): Promise<ShipperAccountManagerResponse> {
  const { data } = await api.patch<ShipperAccountManagerResponse>(
    `/admin/shippers/${shipperProfileId}/account-manager`,
    { opsUserId },
  );
  return data;
}

// ─── Platform settings ───────────────────────────────────────────────────────

export async function getSupportPhone(): Promise<string> {
  const { data } = await api.get<SupportPhoneResponse>('/admin/settings/support-phone');
  return data.supportPhone;
}

/** (M) E.164. */
export async function updateSupportPhone(supportPhone: string): Promise<string> {
  const { data } = await api.put<SupportPhoneResponse>('/admin/settings/support-phone', {
    supportPhone,
  });
  return data.supportPhone;
}

export async function getDefaultMaxOpenOrders(): Promise<number> {
  const { data } = await api.get<DefaultMaxOpenOrdersResponse>(
    '/admin/settings/default-max-open-orders',
  );
  return data.defaultMaxOpenOrders;
}

/** (M) 1..500. */
export async function updateDefaultMaxOpenOrders(defaultMaxOpenOrders: number): Promise<number> {
  const { data } = await api.put<DefaultMaxOpenOrdersResponse>(
    '/admin/settings/default-max-open-orders',
    { defaultMaxOpenOrders },
  );
  return data.defaultMaxOpenOrders;
}

/** App version policy: always six entries, driver/shipper/fleet × android/ios (dnm-work#170 C4). */
export async function getAppVersionPolicy(): Promise<AppVersionPolicyEntry[]> {
  const { data } = await api.get<AppVersionPolicyEntry[]>('/admin/settings/app-version-policy');
  return data;
}

/** (M) One row; 400 APP_VERSION_POLICY_INVALID names the field, 403 MANAGER_ONLY for executives. */
export async function updateAppVersionPolicy(
  app: AppId,
  platform: AppPlatform,
  body: AppVersionPolicyUpdate,
): Promise<AppVersionPolicyEntry> {
  const { data } = await api.put<AppVersionPolicyEntry>(
    `/admin/settings/app-version-policy/${app}/${platform}`,
    body,
  );
  return data;
}

// ─── Ops scorecard (OPS_SCORECARD_PLAN §3) ───────────────────────────────────

export interface ScorecardParams {
  from?: string;
  to?: string;
}

/** Caller's scorecard for period (default: current IST week) + needs attention. */
export async function getMyScorecard(params: ScorecardParams = {}): Promise<OpsScorecardMeResponse> {
  const { data } = await api.get<OpsScorecardMeResponse>('/admin/scorecard/me', { params });
  return data;
}

/** (M) Team scorecard and per-person breakdown. */
export async function getTeamScorecard(params: ScorecardParams = {}): Promise<OpsTeamScorecardResponse> {
  const { data } = await api.get<OpsTeamScorecardResponse>('/admin/scorecard/team', { params });
  return data;
}

export async function getScorecardTargets(): Promise<ScorecardTargets> {
  const { data } = await api.get<ScorecardTargets>('/admin/settings/scorecard-targets');
  return data;
}

/** (M) Validated targets. */
export async function updateScorecardTargets(targets: ScorecardTargets): Promise<ScorecardTargets> {
  const { data } = await api.put<ScorecardTargets>('/admin/settings/scorecard-targets', targets);
  return data;
}

